import React from 'react';
import { EngineCommercialA4Print } from '@/components/print/EngineCommercialPrints';
import {
  buildEpcEstimatePrintLines,
  buildEpcEstimatePrintMeta,
  buildEpcEstimatePrintTotalsHeight,
  formatEpcEstimatePrintAmount,
  type EpcEstimatePrintBundle,
} from '@/utils/epcEstimatePrint';

export interface PrintableEpcEstimateProps {
  bundle: EpcEstimatePrintBundle;
  onClose?: () => void;
}

/** Landscape EST3 estimate printout — BOQ, direct costs, indirects, contingency, commercial totals. */
export const PrintableEpcEstimate: React.FC<PrintableEpcEstimateProps> = ({ bundle, onClose }) => {
  const currency = bundle.currency || 'AED';
  const est = bundle.estimate || {};
  const total = bundle.total || {};
  const pricing = bundle.pricing || {};
  const cont = bundle.contingency || {};

  const tec = Number(total.total_estimated_cost ?? total.tec ?? 0);
  const direct = Number(total.direct_cost ?? 0);
  const indirect = Number(total.indirect_cost ?? 0);
  const contingencyAmt = Number(cont.calculated_amount ?? total.contingency_amount ?? 0);
  const finalBid = Number(
    total.final_bid_price ?? pricing.final_bid_price ?? pricing.bid_price ?? 0
  );
  const marginPct = Number(total.margin_actual ?? pricing.margin_actual ?? pricing.target_margin_percent);
  const markupPct = Number(total.markup_actual ?? pricing.markup_actual ?? 0);

  return (
    <EngineCommercialA4Print
      docType="epc_estimate"
      title="EPC ESTIMATE"
      documentNumber={String(est.estimate_number || 'EST')}
      partyName={String(est.title || bundle.tenderTitle || '')}
      onClose={onClose}
      landscape
      metaLines={buildEpcEstimatePrintMeta(bundle)}
      introText="Detailed estimate printout: client BOQ, direct costs, indirects, contingency, and commercial pricing."
      columns={[
        { key: 'sno', label: '#' },
        { key: 'type', label: 'Type' },
        { key: 'code', label: 'Code / Method' },
        { key: 'description', label: 'Description' },
        { key: 'qty', label: 'Qty', align: 'right' },
        { key: 'uom', label: 'UOM' },
        { key: 'rate', label: 'Rate', align: 'right' },
        { key: 'amount', label: 'Amount', align: 'right' },
      ]}
      lines={buildEpcEstimatePrintLines(bundle)}
      finals={[{ id: 'totals', heightPx: buildEpcEstimatePrintTotalsHeight(bundle) }]}
      finalBlocks={{
        totals: (
          <div className="print-letter-totals">
            <table>
              <tbody>
                <tr>
                  <td>Direct cost :</td>
                  <td>{formatEpcEstimatePrintAmount(direct, currency)}</td>
                </tr>
                <tr>
                  <td>Indirect cost :</td>
                  <td>{formatEpcEstimatePrintAmount(indirect, currency)}</td>
                </tr>
                <tr>
                  <td>Contingency :</td>
                  <td>{formatEpcEstimatePrintAmount(contingencyAmt, currency)}</td>
                </tr>
                <tr>
                  <td>Total estimated cost (TEC) :</td>
                  <td>{formatEpcEstimatePrintAmount(tec, currency)}</td>
                </tr>
                {pricing.pricing_method ? (
                  <tr>
                    <td>Pricing method :</td>
                    <td>{String(pricing.pricing_method).replace(/_/g, ' ')}</td>
                  </tr>
                ) : null}
                {Number.isFinite(marginPct) && marginPct !== 0 ? (
                  <tr>
                    <td>Margin :</td>
                    <td>
                      {marginPct.toLocaleString('en-US', { maximumFractionDigits: 2 })}%
                      {Number.isFinite(markupPct) && markupPct !== 0
                        ? ` (markup ${markupPct.toLocaleString('en-US', { maximumFractionDigits: 2 })}%)`
                        : ''}
                    </td>
                  </tr>
                ) : null}
                <tr className="grand-total">
                  <td>Final bid price :</td>
                  <td>{formatEpcEstimatePrintAmount(finalBid, currency)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        ),
      }}
    />
  );
};
